import dayjs from 'dayjs'
import { useEffect, useState, useRef } from 'react'
import { useForm } from 'react-hook-form'

import { useMainStore } from '@/modules/shared/stores/main.store'
import { useOrderFormStore, useOrderFormStoreActions } from '../../stores/order-form.store'
import { useOrderFormCalculation } from '../../hooks/use-order-form-calculation.hook'
import { useOrderFormComponentStore } from '../../stores/order-form-component.store'
import useOrderFormParams from '../../hooks/use-order-form-params.hook'
import type { IOrderFormJson } from '../../types/order-form-json.type'

export type OrderFormData = {
  fkFrete: string
  vendedor: string
  porcComissao: number
  fkOperacao: string
  fkTipoFaturamento: string
  nPedidoCliente: string
  fkTabelaPreco: string
  dataEmissao: string
  dataEntrega: string
}

export const useOrderDataComponent = () => {
  const {
    jsonOrderForm,
    paramsOrderForm,
    entityTypeSelected,
    isCalculationOrder,
    isOpenModalSelectSeller,
  } = useOrderFormStore()

  const selectedDate = useMainStore(state => state.selectedDate)

  const [isNewOrder] = useState(() => !jsonOrderForm?.idPedido)

	const {calculateOrderForm} = useOrderFormCalculation()

  const [userModifiedDates, setUserModifiedDates] = useState({
    emissao: false,
    entrega: false
  });

  const [userDates, setUserDates] = useState({
    emissao: '',
    entrega: ''
  });

  const isInitialized = useRef(false);

  const formatDateToString = (date: string | number | Date | dayjs.Dayjs) => {
    if (!date) return '';
    return dayjs.utc(date).locale('pt-br').format('YYYY-MM-DD');
  }

  const determineEmissaoDate = () => {
    if (userModifiedDates.emissao && userDates.emissao) {
      return userDates.emissao;
    }

    return jsonOrderForm?.dataEmissao
      ? formatDateToString(jsonOrderForm.dataEmissao)
      : formatDateToString(new Date());
  };

  const determineEntregaDate = () => {
    if (userModifiedDates.entrega && userDates.entrega) {
      return userDates.entrega;
    }

    if (isNewOrder && selectedDate) {
      return formatDateToString(selectedDate);
    }

    return jsonOrderForm?.dataEntrega
      ? formatDateToString(jsonOrderForm.dataEntrega)
      : formatDateToString(new Date());
  };

  const [formValues, setFormValues] = useState(() => ({
    fkFrete: jsonOrderForm?.fkFrete || '',
    vendedor: jsonOrderForm?.vendedor?.razaoSocialNome || '',
    porcComissao: jsonOrderForm?.porcComissao || 0,
    fkOperacao: jsonOrderForm?.fkOperacao || '',
    fkTipoFaturamento: jsonOrderForm?.fkTipoFaturamento || '',
    nPedidoCliente: jsonOrderForm?.nPedidoCliente || '',
    fkTabelaPreco: jsonOrderForm?.fkTabelaPreco || '',
    dataEmissao: determineEmissaoDate(),
    dataEntrega: determineEntregaDate(),
  }));

  useEffect(() => {
    setFormValues(() => ({
      fkFrete: jsonOrderForm?.fkFrete || '',
      vendedor: jsonOrderForm?.vendedor?.razaoSocialNome || '',
      porcComissao: jsonOrderForm?.porcComissao || 0,
      fkOperacao: jsonOrderForm?.fkOperacao || '',
      fkTipoFaturamento: jsonOrderForm?.fkTipoFaturamento || '',
      nPedidoCliente: jsonOrderForm?.nPedidoCliente || '',
      fkTabelaPreco: jsonOrderForm?.fkTabelaPreco || '',
      dataEmissao: determineEmissaoDate(),
      dataEntrega: determineEntregaDate(),
    }));
  }, [jsonOrderForm, selectedDate, userModifiedDates, userDates]);

  const form = useForm<OrderFormData>({
    values: formValues,
  });

  useEffect(() => {
    form.reset(formValues);
  }, [formValues, form]);

  const { isOpenOrderFormModalEntity, isOpenModalChangeCustomerData } =
    useOrderFormComponentStore()

  const { setJsonOrderForm, setIsCalculationOrder, setIsCalculationComissionOrder} = useOrderFormStoreActions()
  const {
    hasClientSelected,
    isEnabledCommission,
		autoCalculateEnabled,
    isVisibleCommission,
    isVisibleTransport,
    isViewOrder,
		isEnabledBillingType,
		isEnabledShippingType,
    isActiveFieldSelectSeller,
		permiteAlteraDataEmissao,
  } = useOrderFormParams()

  const operationTypeOfOrder = {
    1: paramsOrderForm?.listaOperacaoJuridica,
    2: paramsOrderForm?.listaOperacaoFisica,
  }
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null)

  const handleChangeValues = (data: Partial<IOrderFormJson>) => {
    const processedData = { ...data };

    if (processedData.dataEmissao) {
      const formattedDate = formatDateToString(processedData.dataEmissao);
      processedData.dataEmissao = formattedDate;

      setUserModifiedDates(prev => ({ ...prev, emissao: true }));
      setUserDates(prev => ({ ...prev, emissao: formattedDate }));
    }

    if (processedData.dataEntrega) {
      const formattedDate = formatDateToString(processedData.dataEntrega);
      processedData.dataEntrega = formattedDate;

      setUserModifiedDates(prev => ({ ...prev, entrega: true }));
      setUserDates(prev => ({ ...prev, entrega: formattedDate }));
    }

    const newJsonOrderForm = {
      ...jsonOrderForm,
      ...processedData,
    }

    setJsonOrderForm(newJsonOrderForm)

    const shouldCalculateOrder =
      autoCalculateEnabled || processedData.fkTipoFaturamento !== undefined

    if (shouldCalculateOrder) {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current)
      }

      if (!autoCalculateEnabled) {
        if (!isCalculationOrder) setIsCalculationOrder(true)
        calculateOrderForm(newJsonOrderForm)
        return
      }

      debounceTimeout.current = setTimeout(() => {
        if (!isCalculationOrder) setIsCalculationOrder(true)
        calculateOrderForm(newJsonOrderForm)
      }, 750)
    }
  }

  useEffect(() => {
    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current)
      }
    }
  }, [])

  useEffect(() => {
    if (!isInitialized.current && isNewOrder && selectedDate && jsonOrderForm && !userModifiedDates.entrega) {
      const dateString = formatDateToString(selectedDate);

      if (dateString !== jsonOrderForm.dataEntrega) {
        handleChangeValues({
          dataEntrega: dateString,
        });
        isInitialized.current = true;
      }
    }
  }, [selectedDate, isNewOrder, jsonOrderForm?.dataEntrega, userModifiedDates.entrega])

  return {
    hasClientSelected,
    isActiveFieldSelectSeller,
    isOpenModalEntity: isOpenOrderFormModalEntity,
    isOpenModalChangeCustomerData,
    isViewOrder,
    freightType: paramsOrderForm?.listaFrete,
    operationTypeOrder: operationTypeOfOrder[entityTypeSelected],
    billingType: paramsOrderForm?.listaTipoFaturamento,
		priceTableList: paramsOrderForm?.listaTabelaPreco,
    isVisibleTransport,
    isEnabledCommission,
		isEnabledBillingType,
		isEnabledShippingType,
    isVisibleCommission,
    form,
    handleChangeValues,
    isOpenModalSelectSeller,
		setIsCalculationComissionOrder,
    formatDateToString,
		permiteAlteraDataEmissao,
  }
}
